import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  SlaPolicyEditForm,
  SlaPolicyEditInitialValues,
  SlaPolicyComponents,
  SlaPolicyAdditionalParams,
  SlaPolicyFormOptions,
  onSlaPolicyFormSaved,
  ISlaPolicyFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedSlaPolicy, ISharedPatchSlaPolicy, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchSlaPolicy, patchSlaPolicy, getSlaPolicyUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, useOrganizations, useOperationalHourses, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedSlaPolicy> {}

const SlaPolicyFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const organizations = useOrganizations();
  const operational_hourses = useOperationalHourses();

  useEffect(() => {
    fetchSlaPolicy(id).then(sla_policy => setValue(sla_policy));
  }, [id, setValue, ]);

  const param_props: ISlaPolicyFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    organizations: organizations.organizations,
    operational_hourses: operational_hourses.operational_hourses,
  };
  const formLayout: IForm | undefined = useMemo(() => value && SlaPolicyEditForm(value, param_props, t), [value, organizations, operational_hourses, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && SlaPolicyFormOptions(value, param_props, t), [value, organizations, operational_hourses, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchSlaPolicy, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchSlaPolicy) => value && save(() => patchSlaPolicy(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onSlaPolicyFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded || !operational_hourses.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(value.organization_id, CrudEnum.Update, PermissionEnum.SlaPolicy, value)}
    >
      <AtlasForm<ISharedPatchSlaPolicy>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={SlaPolicyComponents}
        additionalParams={SlaPolicyAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={SlaPolicyEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('SLA_POLICY')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const SlaPolicyFormEditView = React.memo(withEdit(SlaPolicyFormEdit, getSlaPolicyUpdateSchema));
